import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  Clock,
  HelpCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { KpiCard } from '../components/ui/KpiCard';
import { formatBRL } from '../utils/FormatDecimal';
import {
  getAccounts,
  getReconciliationSummary,
  getReconciliationTransactions,
  markPostings,
  reconcileBatch,
  ReconciliationSummary,
  ReconciliationPosting,
} from '../services/apiService';

type StatusFilter = 'all' | 'uncleared' | 'pending' | 'cleared';

const statusBadge: Record<string, { label: string; className: string }> = {
  cleared: { label: 'Conciliado', className: 'bg-green-100 text-green-700' },
  pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-700' },
  uncleared: { label: 'Novo', className: 'bg-gray-100 text-gray-600' },
};

export default function ConciliacaoPage() {
  const [accounts, setAccounts] = useState<string[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null);
  const [postings, setPostings] = useState<ReconciliationPosting[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [statementBalance, setStatementBalance] = useState('');
  const [sortField, setSortField] = useState<'date' | 'amount'>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState('');

  // Load accounts on mount
  useEffect(() => {
    getAccounts()
      .then((res) => {
        setAccounts(res.accounts);
        if (res.accounts.length > 0 && !selectedAccount) {
          // Pick first asset/bank account if available
          const bank = res.accounts.find((a: string) => a.startsWith('Ativos:Banco'));
          setSelectedAccount(bank || res.accounts[0]);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    setError('');
    setSelected(new Set());
    try {
      const [summaryRes, txRes] = await Promise.all([
        getReconciliationSummary(selectedAccount),
        getReconciliationTransactions({
          account: selectedAccount,
          status: statusFilter,
          limit: 500,
        }),
      ]);
      setSummary(summaryRes);
      setPostings(txRes.postings);
      setTotal(txRes.total);
    } catch {
      setError('Erro ao carregar dados de conciliação');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sorting
  const sortedPostings = [...postings].sort((a, b) => {
    if (sortField === 'date') {
      const cmp = a.date.localeCompare(b.date);
      return sortAsc ? cmp : -cmp;
    }
    const cmp = a.amount - b.amount;
    return sortAsc ? cmp : -cmp;
  });

  // Selection helpers
  const postingKey = (p: ReconciliationPosting) => `${p.file}:${p.line}`;

  const toggleSelect = (p: ReconciliationPosting) => {
    const key = postingKey(p);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === sortedPostings.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sortedPostings.map(postingKey)));
    }
  };

  const selectedPostings = sortedPostings.filter((p) => selected.has(postingKey(p)));
  const selectedTotal = selectedPostings.reduce((sum, p) => sum + p.amount, 0);

  // Actions
  const handleMark = async (newStatus: 'pending' | 'cleared' | 'uncleared') => {
    if (selectedPostings.length === 0) return;
    setActionLoading(true);
    setFeedback('');
    try {
      const items = selectedPostings.map((p) => ({
        file: p.file,
        line: p.line,
        new_status: newStatus,
      }));
      const result = await markPostings(items);
      setFeedback(`${result.changed} lançamento(s) alterado(s)`);
      await fetchData();
    } catch {
      setFeedback('Erro ao alterar estado');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReconcile = async () => {
    if (selectedPostings.length === 0) return;
    const balance = parseFloat(statementBalance.replace(/\./g, '').replace(',', '.'));
    if (isNaN(balance)) {
      setFeedback('Informe o saldo do extrato para conciliar');
      return;
    }
    setActionLoading(true);
    setFeedback('');
    try {
      const result = await reconcileBatch({
        account: selectedAccount,
        statement_balance: balance,
        posting_ids: selectedPostings.map((p) => ({ file: p.file, line: p.line })),
      });
      setFeedback(
        `Conciliação concluída. Saldo: ${formatBRL(result.cleared_balance)}`
      );
      setSelected(new Set());
      await fetchData();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail && detail.difference !== undefined) {
        setFeedback(
          `Saldo não bate! Diferença: ${formatBRL(detail.difference)}. ` +
          `Cleared: ${formatBRL(detail.cleared_balance)}, Extrato: ${formatBRL(detail.statement_balance)}`
        );
      } else {
        setFeedback('Erro na conciliação');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleSort = (field: 'date' | 'amount') => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  // Statement balance difference
  const statementNum = parseFloat(statementBalance.replace(/\./g, '').replace(',', '.'));
  const hasDifference = !isNaN(statementNum) && summary;
  const difference = hasDifference ? summary!.cleared_balance - statementNum : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Conciliação Bancária</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Account selector */}
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Conta</label>
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[250px]"
          >
            {accounts.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Saldo do Extrato</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Ex: 15000,00"
              value={statementBalance}
              onChange={(e) => setStatementBalance(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-40"
            />
            {difference !== null && (
              <span
                className={`text-sm font-medium px-2 py-1 rounded ${
                  Math.abs(difference) < 0.01
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {Math.abs(difference) < 0.01
                  ? 'OK'
                  : `Diferença: ${formatBRL(difference)}`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Summary KPI cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Conciliado (*)"
            value={formatBRL(summary.cleared_balance)}
            subtitle={`${summary.cleared_count} transações`}
            icon={<CheckCircle className="h-5 w-5" />}
            color="green"
          />
          <KpiCard
            title="Pendente (!)"
            value={formatBRL(summary.pending_balance)}
            subtitle={`${summary.pending_count} transações`}
            icon={<Clock className="h-5 w-5" />}
            color="orange"
          />
          <KpiCard
            title="Não-conferido"
            value={formatBRL(summary.uncleared_balance)}
            subtitle={`${summary.uncleared_count} transações`}
            icon={<HelpCircle className="h-5 w-5" />}
            color="blue"
          />
          <KpiCard
            title={hasDifference ? 'Diferença' : 'Saldo Esperado'}
            value={
              hasDifference
                ? formatBRL(difference!)
                : formatBRL(summary.expected_balance)
            }
            subtitle={
              hasDifference
                ? 'Cleared - Extrato'
                : 'Cleared + Pending + Novo'
            }
            color={
              hasDifference
                ? Math.abs(difference!) < 0.01
                  ? 'green'
                  : 'red'
                : 'purple'
            }
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'uncleared', 'pending', 'cleared'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              statusFilter === s
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {s === 'all' && 'Todos'}
            {s === 'uncleared' && 'Não-conferidos'}
            {s === 'pending' && 'Pendentes'}
            {s === 'cleared' && 'Conciliados'}
          </button>
        ))}
        <span className="text-sm text-gray-500 self-center ml-2">
          {total} transações
        </span>
      </div>

      {/* Transactions table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Carregando...</div>
      ) : sortedPostings.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Nenhuma transação encontrada</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selected.size === sortedPostings.length && sortedPostings.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th
                  className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                  onClick={() => handleSort('date')}
                >
                  Data {sortField === 'date' && (sortAsc ? '↑' : '↓')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Descrição
                </th>
                <th
                  className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                  onClick={() => handleSort('amount')}
                >
                  Valor {sortField === 'amount' && (sortAsc ? '↑' : '↓')}
                </th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedPostings.map((p) => {
                const key = postingKey(p);
                const isExpanded = expandedRow === key;
                const badge = statusBadge[p.status] || statusBadge.uncleared;
                return (
                  <React.Fragment key={key}>
                    <tr
                      className={`hover:bg-blue-50 cursor-pointer ${
                        selected.has(key) ? 'bg-blue-50' : 'even:bg-gray-100'
                      }`}
                      onClick={() => setExpandedRow(isExpanded ? null : key)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          onChange={() => toggleSelect(p)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        <span className="flex items-center gap-1">
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-3 w-3 text-gray-400" />
                          )}
                          {p.date}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{p.payee}</td>
                      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                        <span className={p.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatBRL(p.amount)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.className}`}>
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-8 py-3">
                          <div className="text-xs text-gray-500 space-y-1">
                            <p>
                              <span className="font-medium">Arquivo:</span> {p.file} (linha {p.line})
                            </p>
                            {p.contra_accounts.length > 0 && (
                              <div>
                                <span className="font-medium">Contra-contas:</span>
                                <ul className="ml-4 mt-1">
                                  {p.contra_accounts.map((ca, i) => (
                                    <li key={i}>
                                      {ca.account}: {formatBRL(ca.amount)}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sticky action bar */}
      {sortedPostings.length > 0 && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 shadow-lg rounded-t-xl px-4 py-3 flex flex-wrap items-center gap-3 z-10">
          <span className="text-sm text-gray-600">
            {selected.size} selecionada(s) | Total: {formatBRL(selectedTotal)}
          </span>
          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => handleMark('pending')}
              disabled={selected.size === 0 || actionLoading}
              className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Marcar Pending (!)
            </button>
            <button
              onClick={() => handleMark('cleared')}
              disabled={selected.size === 0 || actionLoading}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Conciliar (*)
            </button>
            <button
              onClick={() => handleMark('uncleared')}
              disabled={selected.size === 0 || actionLoading}
              className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Desmarcar
            </button>
            <button
              onClick={handleReconcile}
              disabled={selected.size === 0 || actionLoading || !statementBalance}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Marca como cleared e valida contra saldo do extrato"
            >
              Conciliar com Extrato
            </button>
          </div>
          {feedback && (
            <span className="text-sm text-blue-600 w-full mt-1">{feedback}</span>
          )}
        </div>
      )}
    </div>
  );
}
